import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Play, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";

/**
 * COMPANION PAGE — minimal version.
 * Body: large portrait placeholder, ▶ Voice Preview button, simple memory list.
 *
 * Relationship/affection levels are intentionally NOT shown — the spec says
 * relationship must remain invisible to the user. Memory list is just titles.
 */

const PREVIEW_TEXT_BY_CHARACTER = {
  seriny: "Hello, darling. [pause] Nice to see you again.",
  elizabeth: "Hello, dear. [pause] Welcome home.",
};

export default function CompanionPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [character, setCharacter] = useState(null);
  const [memories, setMemories] = useState([]);
  const [loadingMem, setLoadingMem] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/catalog/characters").then((r) => {
      const found = (r.data || []).find((c) => c.id === id);
      setCharacter(found || null);
    });
  }, [id]);

  // Fetch memories scoped to this companion. The backend's /memories endpoint
  // returns the active companion's memories — switch active companion first,
  // then fetch, then switch back if it changed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || !id) return;
      setLoadingMem(true);
      try {
        const originalActive = user.character_id;
        if (originalActive !== id) {
          await api.patch("/profile/companion", { character_id: id });
        }
        const { data } = await api.get("/memories");
        if (cancelled) return;
        setMemories(data?.memories || []);
        // Restore original active companion if it differed.
        if (originalActive && originalActive !== id) {
          await api.patch("/profile/companion", { character_id: originalActive });
        }
        await refresh();
      } catch {
        if (!cancelled) setMemories([]);
      } finally {
        if (!cancelled) setLoadingMem(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const previewText = useMemo(() => PREVIEW_TEXT_BY_CHARACTER[id] || "Hello.", [id]);

  const onVoicePreview = async () => {
    if (previewing) return;
    setPreviewing(true);
    try {
      const { data } = await api.post("/tts/synthesize", {
        character_id: id,
        text: previewText,
      });
      const filename = data?.filename || (data?.hash ? `${data.hash}.mp3` : null);
      if (!filename) throw new Error("No audio returned.");
      if (audioRef.current) {
        audioRef.current.src = `${API}/audio/${filename}`;
        await audioRef.current.play();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Voice preview unavailable just now.";
      toast.error(msg);
      setPreviewing(false);
    }
  };

  return (
    <AppShell>
      <main className="max-w-md mx-auto px-6 pt-2 pb-14">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="companion-back-button"
          aria-label="Back"
          className="mb-5 p-2 -ml-2 text-violet-200/70 hover:text-violet-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        {/* Portrait placeholder */}
        <div
          className="aspect-square w-full rounded-2xl overflow-hidden border border-violet-200/15 bg-violet-200/4"
          data-testid="companion-portrait"
        >
          {character?.image ? (
            <img
              src={character.image}
              alt={character?.name || "Companion"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-violet-300/40 text-xs uppercase tracking-[0.3em]">
              Portrait
            </div>
          )}
        </div>

        <h1
          className="mt-6 font-serif-display text-3xl text-violet-50"
          data-testid="companion-name"
        >
          {character?.name || "—"}
        </h1>
        {character?.species && (
          <p
            className="mt-1 text-xs uppercase tracking-[0.32em] text-violet-300/55"
            data-testid="companion-species"
          >
            {character.species}
          </p>
        )}

        {/* Voice Preview */}
        <button
          type="button"
          onClick={onVoicePreview}
          disabled={previewing}
          data-testid="companion-voice-preview"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-violet-200/25 bg-violet-200/6 text-violet-50 text-sm hover:bg-violet-200/12 hover:border-violet-200/40 transition-colors disabled:opacity-60"
        >
          {previewing ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Play className="w-4 h-4 translate-x-[1px]" strokeWidth={1.5} />
          )}
          <span>Voice preview</span>
        </button>
        <audio
          ref={audioRef}
          onEnded={() => setPreviewing(false)}
          onError={() => setPreviewing(false)}
          className="hidden"
          data-testid="companion-voice-preview-audio"
        />

        {/* Memories */}
        <section className="mt-10">
          <h2 className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-3">
            Memories
          </h2>
          {loadingMem ? (
            <p className="text-violet-300/50 text-sm">…</p>
          ) : memories.length === 0 ? (
            <p
              className="text-violet-300/45 text-sm italic"
              data-testid="companion-memories-empty"
            >
              No shared memories yet.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="companion-memories-list">
              {memories.map((m) => {
                const linkable = !!m.session_id;
                const inner = (
                  <>
                    <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-violet-300/55 flex-shrink-0" />
                    <span className="flex-1">{m.title || "Untitled memory"}</span>
                    {m.audio_ready && (
                      <span
                        className="text-[9px] uppercase tracking-[0.28em] text-violet-300/50"
                        aria-label="audio available"
                      >
                        ▶
                      </span>
                    )}
                  </>
                );
                return (
                  <li key={m.id} data-testid={`companion-memory-${m.id}`}>
                    {linkable ? (
                      <button
                        type="button"
                        onClick={() => nav(`/sessions/${m.session_id}`)}
                        data-testid={`companion-memory-${m.id}-link`}
                        className="w-full text-left flex items-start gap-2 text-violet-100/85 text-sm hover:text-violet-50 transition-colors py-1"
                      >
                        {inner}
                      </button>
                    ) : (
                      <div className="flex items-start gap-2 text-violet-100/85 text-sm py-1">
                        {inner}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </AppShell>
  );
}
